import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { AdminService } from '../../services/admin.service';
import { AppFeatureStateService } from '../../services/app-feature-state.service';
import { APP_MENU_FEATURE_KEYS } from '../../constants/app-menu.constants';

const APP_ROUTE_BY_FEATURE: Record<string, string> = {
  [APP_MENU_FEATURE_KEYS.PROJECTS]: '/projects',
  [APP_MENU_FEATURE_KEYS.HOME]: '/home',
  [APP_MENU_FEATURE_KEYS.USER_MANAGEMENT]: '/users',
  [APP_MENU_FEATURE_KEYS.ROLE_MANAGEMENT]: '/role-management',
  [APP_MENU_FEATURE_KEYS.TEMPLATES]: '/templates',
  [APP_MENU_FEATURE_KEYS.TASK_MANAGEMENT]: '/task-management',
  [APP_MENU_FEATURE_KEYS.FILE_SETTINGS]: '/file-settings',
  [APP_MENU_FEATURE_KEYS.FILES]: '/app-files',
  [APP_MENU_FEATURE_KEYS.PRIVACY_POLICY]: '/privacy-policy-admin',
  [APP_MENU_FEATURE_KEYS.INTEGRATIONS]: '/integrations',
  [APP_MENU_FEATURE_KEYS.BLOCK_SCHEDULE]: '/block-schedule',
  [APP_MENU_FEATURE_KEYS.MAPS]: '/maps'
};

const FALLBACK_FEATURE_ORDER = [
  APP_MENU_FEATURE_KEYS.PROJECTS,
  APP_MENU_FEATURE_KEYS.HOME,
  APP_MENU_FEATURE_KEYS.TEMPLATES,
  APP_MENU_FEATURE_KEYS.TASK_MANAGEMENT,
  APP_MENU_FEATURE_KEYS.USER_MANAGEMENT,
  APP_MENU_FEATURE_KEYS.FILE_SETTINGS,
  APP_MENU_FEATURE_KEYS.FILES,
  APP_MENU_FEATURE_KEYS.PRIVACY_POLICY,
  APP_MENU_FEATURE_KEYS.INTEGRATIONS,
  APP_MENU_FEATURE_KEYS.BLOCK_SCHEDULE,
  APP_MENU_FEATURE_KEYS.MAPS,
  APP_MENU_FEATURE_KEYS.ROLE_MANAGEMENT
];

function resolveFallbackUrlTree(router: Router, appFeatureState: AppFeatureStateService, requestedFeatureKey?: string): UrlTree {
  const fallbackFeature = FALLBACK_FEATURE_ORDER.find(featureKey =>
    featureKey !== requestedFeatureKey && appFeatureState.canView(featureKey)
  );

  if (fallbackFeature) {
    return router.parseUrl(APP_ROUTE_BY_FEATURE[fallbackFeature]);
  }

  return router.parseUrl('/login');
}

export const appFeaturePermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const adminService = inject(AdminService);
  const appFeatureState = inject(AppFeatureStateService);
  const router = inject(Router);
  const featureKey = route.data?.['featureKey'] as string | undefined;

  return adminService.getUserAdminFeaturePermissions().pipe(
    map(keys => {
      appFeatureState.setPermissions(keys);

      if (!featureKey) {
        return true;
      }

      if (appFeatureState.canView(featureKey)) {
        return true;
      }

      return resolveFallbackUrlTree(router, appFeatureState, featureKey);
    }),
    catchError(() => {
      appFeatureState.setPermissions([]);
      return of(featureKey ? resolveFallbackUrlTree(router, appFeatureState, featureKey) : router.parseUrl('/login'));
    }),
    take(1)
  );
};
